import { supabase } from "@/integrations/supabase/client";

export interface Product {
  id: string;
  name: string;
  description: string | null;
  version: string;
  download_url: string;
  price: number;
  max_activations: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface License {
  id: string;
  user_id: string;
  product_id: string;
  license_key: string;
  max_activations: number;
  current_activations: number;
  status: 'active' | 'suspended' | 'revoked';
  expires_at: string | null;
  created_at: string;
  updated_at: string;
  products?: Product;
}

export interface LicenseActivation {
  id: string;
  license_id: string;
  machine_id: string;
  machine_name: string | null;
  ip_address: string | null;
  os_info: string | null;
  activated_at: string;
  last_validated_at: string;
  is_active: boolean;
}

export interface Order {
  id: string;
  user_id: string;
  product_id: string;
  license_id: string | null;
  amount: number;
  status: 'pending' | 'completed' | 'failed' | 'refunded';
  payment_method: string | null;
  payment_proof_url: string | null;
  created_at: string;
  updated_at: string;
  products?: Product;
  licenses?: License;
}

export interface CreateOrderWithProofResult {
  success: boolean;
  orderIds?: string[];
  error?: string;
}

/**
 * Get all active products
 */
export async function getProducts(): Promise<Product[]> {
  try {
    const { data, error } = await supabase
      .from('products')
      .select('*')
      .eq('is_active', true)
      .order('name');

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error getting products:', error);
    return [];
  }
}

/**
 * Get user's licenses with product details
 */
export async function getUserLicenses(userId: string): Promise<License[]> {
  try {
    const { data, error } = await supabase
      .from('licenses')
      .select(`
        *,
        products (*)
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error getting user licenses:', error);
    return [];
  }
}

/**
 * Get license activations
 */
export async function getLicenseActivations(licenseId: string): Promise<LicenseActivation[]> {
  try {
    const { data, error } = await supabase
      .from('license_activations')
      .select('*')
      .eq('license_id', licenseId)
      .eq('is_active', true)
      .order('activated_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error getting license activations:', error);
    return [];
  }
}

/**
 * Create order for product purchase
 */
export async function createOrder(
  userId: string,
  productId: string,
  paymentMethod: string = 'bank_transfer'
): Promise<Order | null> {
  try {
    const { data, error } = await supabase
      .from('orders')
      .insert({
        user_id: userId,
        product_id: productId,
        amount: 0, // Will be updated with actual product price
        status: 'pending',
        payment_method: paymentMethod
      })
      .select('*, products(*)')
      .single();

    if (error) throw error;

    // Update amount with product price
    if (data && data.products) {
      await supabase
        .from('orders')
        .update({ amount: (data.products as any).price })
        .eq('id', data.id);
    }

    return data;
  } catch (error) {
    console.error('Error creating order:', error);
    return null;
  }
}

/**
 * Complete order and generate license
 */
export async function completeOrder(orderId: string): Promise<{
  success: boolean;
  license?: License;
  error?: string;
}> {
  try {
    // Get order details
    const { data: order, error: orderError } = await supabase
      .from('orders')
      .select('*, products(*)')
      .eq('id', orderId)
      .single();

    if (orderError || !order) {
      return { success: false, error: 'Order not found' };
    }

    // Generate license key using database function
    const { data: keyData, error: keyError } = await supabase
      .rpc('generate_license_key', { product_id_param: order.product_id });

    if (keyError || !keyData) {
      return { success: false, error: 'Failed to generate license key' };
    }

    const licenseKey = keyData as string;

    // Create license
    const { data: license, error: licenseError } = await supabase
      .from('licenses')
      .insert({
        user_id: order.user_id,
        product_id: order.product_id,
        license_key: licenseKey,
        max_activations: (order.products as any).max_activations || 2,
        status: 'active'
      })
      .select('*, products(*)')
      .single();

    if (licenseError || !license) {
      return { success: false, error: 'Failed to create license' };
    }

    // Update order with license
    await supabase
      .from('orders')
      .update({
        license_id: license.id,
        status: 'completed'
      })
      .eq('id', orderId);

    return { success: true, license };
  } catch (error: any) {
    console.error('Error completing order:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Validate license and activate machine
 */
export async function validateAndActivateLicense(
  licenseKey: string,
  machineId: string,
  machineName?: string,
  ipAddress?: string,
  osInfo?: string
): Promise<{
  success: boolean;
  message?: string;
  error?: string;
  activationsUsed?: number;
  maxActivations?: number;
}> {
  try {
    const { data, error } = await supabase.rpc('validate_and_activate_license', {
      license_key_param: licenseKey,
      machine_id_param: machineId,
      machine_name_param: machineName || null,
      ip_address_param: ipAddress || null,
      os_info_param: osInfo || null
    });

    if (error) throw error;

    const result = data as any;
    return result;
  } catch (error: any) {
    console.error('Error validating license:', error);
    return {
      success: false,
      error: error.message || 'Failed to validate license'
    };
  }
}

/**
 * Reset license (deactivate all machines)
 */
export async function resetLicense(licenseKey: string): Promise<{
  success: boolean;
  message?: string;
  error?: string;
}> {
  try {
    const { data, error } = await supabase.rpc('reset_license', {
      license_key_param: licenseKey
    });

    if (error) throw error;

    const result = data as any;
    return result;
  } catch (error: any) {
    console.error('Error resetting license:', error);
    return {
      success: false,
      error: error.message || 'Failed to reset license'
    };
  }
}

/**
 * Get user's orders with details
 */
export async function getUserOrders(userId: string): Promise<Order[]> {
  try {
    const { data, error } = await supabase
      .from('orders')
      .select(`
        *,
        products (*),
        licenses (*)
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error getting user orders:', error);
    return [];
  }
}

/**
 * Get order by ID
 */
export async function getOrder(orderId: string): Promise<Order | null> {
  try {
    const { data, error } = await supabase
      .from('orders')
      .select(`
        *,
        products (*),
        licenses (*)
      `)
      .eq('id', orderId)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error getting order:', error);
    return null;
  }
}

/**
 * Create order by product name (instead of UUID)
 */
export async function createOrderByProductName(
  userId: string,
  productName: string,
  paymentMethod: string = 'bank_transfer'
): Promise<Order | null> {
  try {
    // Get product ID by name
    const { data: productId, error: productError } = await supabase
      .rpc('get_product_id_by_name', { product_name_param: productName });

    if (productError || !productId) {
      console.error('Product not found:', productName);
      return null;
    }

    return await createOrder(userId, productId, paymentMethod);
  } catch (error) {
    console.error('Error creating order by product name:', error);
    return null;
  }
}

/**
 * Complete multiple orders and generate licenses for cart items
 */
export async function completeCartPurchase(
  userId: string,
  cartItems: { name: string; price: number }[]
): Promise<{
  success: boolean;
  licenses?: License[];
  error?: string;
}> {
  try {
    const licenses: License[] = [];
    const errors: string[] = [];

    for (const item of cartItems) {
      // Create order
      const order = await createOrderByProductName(userId, item.name, 'bank_transfer');

      if (!order) {
        errors.push(`Failed to create order for ${item.name}`);
        continue;
      }

      // Complete order and generate license
      const result = await completeOrder(order.id);

      if (!result.success || !result.license) {
        errors.push(`Failed to generate license for ${item.name}: ${result.error}`);
        continue;
      }

      licenses.push(result.license);
    }

    if (licenses.length === 0) {
      return {
        success: false,
        error: errors.join('; ')
      };
    }

    return {
      success: true,
      licenses
    };
  } catch (error: any) {
    console.error('Error completing cart purchase:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Create orders for cart items and attach a payment proof URL.
 */
export async function createCartOrdersWithProof(
  userId: string,
  cartItems: { name: string; price: number }[],
  paymentProofUrl: string,
  paymentMethod: string = 'bank_transfer'
): Promise<CreateOrderWithProofResult> {
  try {
    const orderIds: string[] = [];
    const errors: string[] = [];

    for (const item of cartItems) {
      const order = await createOrderByProductName(userId, item.name, paymentMethod);

      if (!order) {
        errors.push(`Failed to create order for ${item.name}`);
        continue;
      }

      const { error: updateError } = await supabase
        .from('orders')
        .update({ payment_proof_url: paymentProofUrl })
        .eq('id', order.id);

      if (updateError) {
        errors.push(`Failed to attach slip for ${item.name}: ${updateError.message}`);
        continue;
      }

      orderIds.push(order.id);
    }

    if (orderIds.length === 0) {
      return { success: false, error: errors.join('; ') };
    }

    if (errors.length > 0) {
      return { success: false, orderIds, error: errors.join('; ') };
    }

    return { success: true, orderIds };
  } catch (error: any) {
    console.error('Error creating orders with proof:', error);
    return { success: false, error: error.message };
  }
}
